import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAPI } from './APIContext';
import { useGeo } from './GeoContext';
import { useStorage } from './StorageContext';

export const JourneyContext = createContext();

export const JourneyProvider = ({ children }) => {

  const PHASE_BLANK = "Blank";
  const PHASE_SIGNIN = "Signin";
  const PHASE_PERMISSIONS = "Permissions";
  const PHASE_OVERTURE = "Overture";
  const PHASE_PRELUDE = "Prelude";
  const PHASE_HUNT = "Hunt";
  const PHASE_FINALE = "Finale";
  const PHASE_CODA = "Coda";

  const storage = useStorage();
  const api = useAPI();
  const geo = useGeo();

  const PHASE_OVERRIDE = null; // Set to null for normal operation, or one of the phase constants to force a specific phase for testing

  // Master loading state for the entire app
  const [isLoading, setIsLoading] = useState(false);
  
  const [appPhase, setAppPhase] = useState(PHASE_BLANK);
  
  const [startingDistance, setStartingDistance] = useState(null);
  const [showingAtLocation, setShowingAtLocation] = useState(false);
  const [seenItemsByLocation, setSeenItemsByLocation] = useState({});
  
  // Timer tick counter for components that need to sync with our timer
  const [timerTick, setTimerTick] = useState(0);
    
  // Accessibility state
  const [isShowingLargeText, setIsShowingLargeText] = useState(false);
  const [isShowingClosedCaptioning, setIsShowingClosedCaptioning] = useState(false);
  const [accessibility, setAccessibility] = useState([]);
  
  // Debug state
  const [isDebuggingGPS, setIsDebuggingGPS] = useState(false);
  
  // Track when user came to permissions via GPS prompt (vs normal app flow)
  const [isReturningToPermissions, setIsReturningToPermissions] = useState(false);
  const [previousPhase, setPreviousPhase] = useState(null);

  // Track when journey is complete (no more locations available)
  const [isJourneyComplete, setIsJourneyComplete] = useState(false);
  
  // Track when user has just finished a location to prevent fallback logic from re-selecting it
  const [hasJustFinishedLocation, setHasJustFinishedLocation] = useState(false);

  // Use storage data directly from StorageContext
  const voyagerCode = storage.voyagerCode;
  const visitedLocations = storage.visitedLocations;
  const selectedLocation = storage.selectedLocation;
  const storedCompletedPermissions = storage.hasCompletedPermissions;
  const storedCompletedOverture = storage.hasCompletedOverture;
  const storedCompletedFinale = storage.hasCompletedFinale;
  const serverData = api.serverData;

  
  // Sync loading states - JourneyContext manages the master isLoading state
  useEffect(() => {
    const anyLoading = storage.isLoading || api.isLoading;
    setIsLoading(anyLoading);
  }, [storage.isLoading, api.isLoading]);

  // Reset journey complete state when voyager code changes (new user/journey)
  useEffect(() => {
    if (voyagerCode) {
      setIsJourneyComplete(false);
    } 
  }, [voyagerCode]);

  // Use ref to track if we have an active timer
  const timerIdRef = useRef(null);

  const checkTime = useCallback(() => {
    //console.log("checkTime called");

    // Increment timer tick to notify components that depend on timer updates
    setTimerTick(prev => prev + 1);

    //console.log("Current Phase:", appPhase);
    if (isLoading) {
      return;
    }

    // Only handle time-based transitions when we have serverData and are past signin/permissions
    //console.log("Server data:", api.serverData ? "exists" : "null" );
    //console.log("Voyager code:", voyagerCode);

    if (!voyagerCode) {
      //console.log("Not signed in yet - at default signin phase");
      setAppPhase(PHASE_SIGNIN);
      return;
    }

    if (!storage.hasCompletedPermissions) {
      if (appPhase !== PHASE_PERMISSIONS) {
        setAppPhase(PHASE_PERMISSIONS);
      }
      return;
    }

    // If user is returning to permissions via GPS prompt, don't auto-transition
    if (isReturningToPermissions && appPhase === PHASE_PERMISSIONS) {
      return;
    }

    // Need server data to determine time-based phases
    if (!serverData) {
      //console.log("Waiting for server data to determine phase...");
      return;
    }

    let result = PHASE_HUNT; // Default to hunt phase

    if (PHASE_OVERRIDE !== null) {
      result = PHASE_OVERRIDE;
    } else {
      // Simplified time check logic
      let starttime = serverData.start_at;
      let finishtime = serverData.finish_at;
      let currentTime = new Date();
      
      if (starttime != null && finishtime != null) {
        let startDateTime = new Date(starttime);
        let finishDateTime = new Date(finishtime);

        if (currentTime < startDateTime) {
          result = PHASE_PRELUDE;
        } else if (currentTime >= finishDateTime) {
          // After finish time
          if (storedCompletedFinale) {
            result = PHASE_CODA;
          } else {
            result = PHASE_FINALE;
          }
        } else if (!storage.hasCompletedOverture) {
          // During show but haven't done overture yet
          result = PHASE_OVERTURE;          
        }
        // Otherwise stay in hunt phase (default)
      }
    }

    // Only update appPhase if it's actually different
    if (result !== appPhase) {
      console.log("Phase changed from", appPhase, "to", result);
      setAppPhase(result);
    } else {
      //console.log("Phase remains the same:", appPhase);
    }
  }, [appPhase, isLoading, serverData, voyagerCode, storage.hasCompletedPermissions, storage.hasCompletedOverture, storedCompletedFinale, PHASE_OVERRIDE, isReturningToPermissions]);

  // Use useRef to always call the latest version of checkTime
  const checkTimeRef = useRef();
  checkTimeRef.current = checkTime;

  // Timer effect for phase updates - runs only once, uses ref to always call latest checkTime
  useEffect(() => {
    // Prevent multiple timers
    if (timerIdRef.current) {
      return;
    }

    const interval = setInterval(() => {
      if (checkTimeRef.current) {
        checkTimeRef.current(); // Always calls the latest version of checkTime
      }
    }, 1000);
    
    timerIdRef.current = interval;

    return () => {
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, []); // Empty dependency array - this effect runs only once

  // Fetch server data when voyager code changes
  useEffect(() => {
    const fetchServerData = async () => {      
      if (voyagerCode && !api.serverData && !api.isLoading) {
        try {
          await api.fetchData(voyagerCode);
        } catch (error) {
          console.error("Error fetching server data:", error);
        }
      }
    };

    fetchServerData();
  }, [voyagerCode, api]);

  // Fallback to server's current_target_id if no selectedLocation is stored locally
  useEffect(() => {
    const setupCurrentTargetFallback = async () => {
      // Only run this fallback logic if:
      // 1. Storage has finished loading
      // 2. We have server data
      // 3. We don't have a selectedLocation stored locally
      // 4. Server data has a current_target_id that isn't null
      // 5. User hasn't just finished a location (to prevent re-selecting the same location)
      if (!storage.isLoading && 
          serverData && 
          !selectedLocation && 
          serverData.current_target_id &&
          !hasJustFinishedLocation) {
        
        try {
          const targetLocation = api.getLocationById(serverData.current_target_id);
          
          if (targetLocation) {
            await storage.setSelectedLocation(targetLocation);
          } else {
            console.warn("Server's current_target_id not found in locations data:", serverData.current_target_id);
          }
        } catch (error) {
          console.error("Error setting selectedLocation from server's current_target_id:", error);
        }
      }
    };

    setupCurrentTargetFallback();
  }, [serverData, selectedLocation, storage.isLoading, api, storage, hasJustFinishedLocation]);

  // If we have a selected location, mark overture as completed
  useEffect(() => {
    const markOvertureCompletedIfNeeded = async () => {
      // If we have a selectedLocation and overture hasn't been marked as completed yet,
      // mark it as completed since having a location means we've progressed past overture
      if (!storage.isLoading && 
          selectedLocation && 
          !storedCompletedOverture) {
        
        try {
          await storage.setCompletedOverture(true);
        } catch (error) {
          console.error("Error marking overture as completed:", error);
        }
      }
    };

    markOvertureCompletedIfNeeded();
  }, [selectedLocation, storedCompletedOverture, storage.isLoading, storage]);

  // Process ability_summary from server data to set accessibility preferences
  useEffect(() => {
    const processAbilitySummary = () => {
      if (serverData && serverData.ability_summary && Array.isArray(serverData.ability_summary)) {
        // Store the full ability_summary array for reference by content items
        setAccessibility(serverData.ability_summary);
        
        // Set large text preference if "Big Text" is in the ability_summary
        if (serverData.ability_summary.indexOf("Big Text") > -1) {
          setIsShowingLargeText(true);
        }
        
        // Set closed captioning preference if "Captions" is in the ability_summary
        if (serverData.ability_summary.indexOf("Captions") > -1) {
          setIsShowingClosedCaptioning(true);
        }
      } else if (serverData && serverData.ability_summary === null) {
        // Reset accessibility settings if ability_summary is explicitly null
        setAccessibility([]);
        setIsShowingLargeText(false);
        setIsShowingClosedCaptioning(false);
      }
    };

    processAbilitySummary();
  }, [serverData]);

  // Figure out current phase on initial load - runs once when storage finishes loading
  useEffect(() => {
    // Wait until storage has finished loading
    if (storage.isLoading) {
      return;
    }

    const figureOutCurrentPhase = () => {
      //console.log("Figuring out current phase...");
      //console.log("  - PHASE_OVERRIDE:", PHASE_OVERRIDE);
      //console.log("  - voyagerCode:", voyagerCode);
      //console.log("  - hasCompletedPermissions:", storedCompletedPermissions);
      //console.log("  - hasCompletedOverture:", storedCompletedOverture);
      
      if (PHASE_OVERRIDE !== null) {
        setAppPhase(PHASE_OVERRIDE);
        return;
      } else if (!voyagerCode) {
        setAppPhase(PHASE_SIGNIN);
        return;
      } 

      // If user is signed in, set initial phase based on completion flags
      // checkTime() will then handle time-based transitions
      if (!storedCompletedPermissions) {
        setAppPhase(PHASE_PERMISSIONS);
      } else {
        // Trigger checkTime immediately to set the correct phase
        if (checkTimeRef.current) {
          checkTimeRef.current();
        }
      }
    }

    figureOutCurrentPhase();
  }, [storage.isLoading, PHASE_OVERRIDE, voyagerCode, storedCompletedPermissions, storedCompletedOverture]);

  /* *** Show status changes *** */

  const finishedPermissions = () => {
    console.log("Finished Permissions");
    storage.setCompletedPermissions(true);
  }

  const finishedPrelude = () => {
    console.log("Finished Prelude");
    // Prelude is time-based, no storage needed
  }

  const finishedOverture = () => {
    console.log("Finished Overture");
    storage.setCompletedOverture(true);
  }

  const finishedFinale = () => {
    console.log("Finished Finale");
    storage.setCompletedFinale(true);
  }

  const finishedCoda = () => {
    console.log("Finished Coda");
    // Coda is time-based, no storage needed
  }

  const goToPermissions = () => {
    setPreviousPhase(appPhase); // Store current phase to return to
    setIsReturningToPermissions(true);
    setAppPhase(PHASE_PERMISSIONS);
  }

  const continueFromPermissions = () => {
    setIsReturningToPermissions(false);
    if (previousPhase) {
      setAppPhase(previousPhase);
      setPreviousPhase(null);
    } else {
      // Fallback to normal permissions completion flow
      finishedPermissions();
    }
  }


  /* *** Data helpers *** */
  const codaItems = () => {
    return serverData?.codas || [];
  }

  const finaleItems = () => {
    return serverData?.finales || [];
  }

  const overtureItems = () => {
    return serverData?.overtures || [];
  }

  const preludeItems = () => {
    return serverData?.preludes || [];
  }

  /* *** Location helpers *** */

  const preludeLocation = () => {
    if (serverData?.locations) {
      const result = serverData.locations.find(loc => locationHasTag(loc, "Prelude"));
      if (result) {
        return result;
      }
    }
    return null;
  }

  const finaleLocation = () => {
    if (serverData?.locations) {
      const result = serverData.locations.find(loc => locationHasTag(loc, "Finale"));
      if (result) {
        return result;
      }
    }
    return null;
  }

  const hasAvailableLocations = () => {
    return api.hasAvailableLocations(voyagerCode);
  };

  const resetJourneyComplete = () => {
    setIsJourneyComplete(false);
  };

  // Helper function to check if location has a specific tag
  const locationHasTag = (location, tag) => {
    const result = location.selected_tags_list && location.selected_tags_list.includes(tag);
    return result;
  };

  // Helper function to check if location has already been visited
  const hasAlreadyVisitedLocation = (locationId) => {
    return visitedLocations.includes(locationId);
  };

  const startNextLocation = async () => {
    // Don't try to get next location if journey is already complete
    if (isJourneyComplete) {
      console.log("Journey is complete, no more locations available");
      return null;
    }

    try {
      setIsLoading(true);
      const nextLocation = await api.getNextLocation(voyagerCode, selectedLocation);
      if (nextLocation) {
        await storage.setSelectedLocation(nextLocation);
        setShowingAtLocation(false); // Reset showing state for new location
        // Clear the "just finished" flag since user is starting a new location
        setHasJustFinishedLocation(false);
        const distance = geo.distanceToLocation(nextLocation);
        setStartingDistance(distance);
        return nextLocation;
      } else {
        console.warn("No next location found, checking if journey is complete");
        
        // Use existing hasAvailableLocations infrastructure to determine if we're truly done
        const availabilityStatus = await api.hasAvailableLocations(voyagerCode);
        
        if (availabilityStatus === 'finished') {
          console.log("Journey is complete - no more locations available");
          setIsJourneyComplete(true);
        } else {
          console.log("Locations may be temporarily unavailable, but journey is not complete");
        }
        
        return null;
      }
    } catch (error) {
      console.error("Error getting next location:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  const restartSearch = async () => {
    if (selectedLocation) {
      const distance = geo.distanceToLocation(selectedLocation);
      setStartingDistance(distance);
      setShowingAtLocation(false);
      markAllItemsAsUnseen(selectedLocation.id);
    }
  };

  const getNextLocationLocal = (serverData) => {
    let result = null;
    if (visitedLocations && serverData?.locations) {
      const locationsList = serverData.locations;
      if (visitedLocations.length < locationsList.length) {
        while (result === null) {
          const randomIndex = Math.floor(Math.random() * locationsList.length);
          const randomLocation = locationsList[randomIndex];
          
          if (!hasAlreadyVisitedLocation(randomLocation.id) && 
              !(locationHasTag(randomLocation, "Finale") && !locationHasTag(randomLocation, "Prelude"))) {
            if (randomLocation.audiencelimit !== null && randomLocation.audiencelimit > 5) {
              result = randomLocation;
            }
          }
        }
      }
    }
    return result;
  };

  const visitedLocationObjects = () => {
    const result = [];
    for (const nextId of visitedLocations) {
      const nextLocation = api.getLocationById(nextId);
      // skip prelude locations
      if (nextLocation && !locationHasTag(nextLocation, "Prelude")) {
        result.push(nextLocation);
      }
    }
    return result;
  };

  const arrivedAtLocation = async (location) => {
    await storage.addVisitedLocation(location.id);
    api.sendArrivalNotification(location, voyagerCode);
    setShowingAtLocation(true);
  };

  const markItemAsSeen = (itemId, locationId) => {
    setSeenItemsByLocation(prev => {
      const updated = { ...prev };
      if (!updated[locationId]) {
        updated[locationId] = [];
      }
      updated[locationId] = [...updated[locationId], itemId];
      return updated;
    });
  };

  const markAllItemsAsUnseen = (locationId) => {
    setSeenItemsByLocation(prev => {
      const updated = { ...prev };
      updated[locationId] = [];
      return updated;
    });
  };

  const finishedAtLocation = async () => {
    await storage.setSelectedLocation(null);
    setShowingAtLocation(false);
    // Set flag to prevent fallback logic from immediately re-selecting the same location
    setHasJustFinishedLocation(true);
  }

  const doesItemHaveTag = (item, tag) => {
    let result = false;
    if (!!(item.selected_tags_list)) {
      for (let i=0; i<item.selected_tags_list.length && result === false; i++) {
        if (item.selected_tags_list[i] === tag) {
          result = true;
        }
      }
    }
    return result;
  };

  const isFeedbackItem = (contentitem) => {
    return doesItemHaveTag(contentitem, "Feedback");
  }

  /* *** App flow management *** */

  const isFirstHunt = () => {
    return !visitedLocations || visitedLocations.length === 0;
  };

  const reset = async () => {
    await storage.setSelectedLocation(null);
    setShowingAtLocation(false);
    setSeenItemsByLocation({});
    setHasJustFinishedLocation(false); // Clear the "just finished" flag on reset
    
    // Clear the timer if it exists
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }

    await storage.reset();
    setAppPhase("Permissions");
    api.setServerData(null);
  }

  return (
    <JourneyContext.Provider value={{
      // phases and progress
      appPhase,
      setAppPhase,

      finishedPermissions,
      finishedPrelude,
      finishedOverture,
      finishedFinale,
      finishedCoda,
      goToPermissions,
      continueFromPermissions,
      isReturningToPermissions,

      isLoading,
      setIsLoading,
      voyagerCode,
      setVoyagerCode: storage.setVoyagerCode,

      serverData,
      timerTick,

      startingDistance,
      setStartingDistance,
            
      preludeItems,
      overtureItems,
      codaItems,
      finaleItems,
      hasAvailableLocations,
      startNextLocation,
      selectedLocation,
      setSelectedLocation: storage.setSelectedLocation,
      showingAtLocation,
      setShowingAtLocation,
      isJourneyComplete,
      resetJourneyComplete,
      visitedLocations,
      finishedAtLocation,
      setVisitedLocations: storage.setVisitedLocations,
      seenItemsByLocation,
      setSeenItemsByLocation,
      preludeLocation,
      finaleLocation,
      locationHasTag,
      hasAlreadyVisitedLocation,
      getNextLocationLocal,
      visitedLocationObjects,
      arrivedAtLocation,
      markItemAsSeen,
      doesItemHaveTag,
      isFeedbackItem,
      isFirstHunt,

      // accessibility
      isShowingLargeText,
      setIsShowingLargeText,
      isShowingClosedCaptioning,
      setIsShowingClosedCaptioning,
      accessibility,
      setAccessibility,

      // debug
      isDebuggingGPS,
      setIsDebuggingGPS,

      // reset
      reset,
      restartSearch,
    }}>
      {children}
    </JourneyContext.Provider>
  );
};

// Custom hook for using Journey context
export const useJourney = () => {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error('useJourney must be used within a JourneyProvider');
  }
  return context;
};

export default JourneyProvider;